import { useCallback, useMemo } from "react"
import { AppAuthProviderContextProps } from "../context"
import useAppAuthProviderLoggedUser from "./useAppAuthProvider.loggedUser"
import useAppAuthProviderTokens from "./useAppAuthProvider.tokens"
import useAppAuthProviderTokensRefresh from "./useAppAuthProvider.tokens.refresh"
import { useQueryClient } from "@tanstack/react-query"
import { UserRole } from "../../../../../backend/src/types"
import _ from "lodash"
import { TokensResponse } from "../../../../../backend/src/routes/schemes"
import { usePersonificateUserMutation } from "@/api/auth/personification"

export type useAppAuthProviderProps = React.PropsWithChildren<unknown>
export type AppAuthProviderProps = React.PropsWithChildren<{
    contextProps: AppAuthProviderContextProps
    isBusy: boolean
}>

const useAppAuthProvider = (props: useAppAuthProviderProps): AppAuthProviderProps => {
    const tokensProps = useAppAuthProviderTokens()
    useAppAuthProviderTokensRefresh(tokensProps)

    const { tokens, reset, setTokens } = tokensProps
    const loggedUser = useAppAuthProviderLoggedUser(tokens?.accessToken, reset)

    const client = useQueryClient()
    const onLogout = useCallback(async () => {
        reset()
        client.removeQueries()
    }, [client, reset])

    const onLogin = useCallback(
        async (tokens: TokensResponse) => {
            client.removeQueries()
            setTokens(tokens)
        },
        [client, setTokens]
    )

    const { mutateAsync: personificateUserMutation } = usePersonificateUserMutation(tokens?.accessToken)
    const personificateUser = useCallback(
        async (userId: number) => {
            const tokens = await personificateUserMutation(userId)
            client.removeQueries()
            setTokens(tokens)
        },
        [client, personificateUserMutation, setTokens]
    )

    // null means in all companies
    // undefined means role in any company
    const hasRole = useCallback(
        (role: UserRole | UserRole[], companyId?: number | null) => {
            if (!loggedUser.data) return false
            if (_.isArray(role))
                return loggedUser.data.roles.some(
                    (a) =>
                        role.includes(a.role) &&
                        (companyId === undefined ||
                            a.companyId === companyId ||
                            (companyId !== undefined && !a.companyId) ||
                            (companyId === null && !a.companyId))
                )
            else
                return loggedUser.data.roles.some(
                    (a) =>
                        a.role === role &&
                        (companyId === undefined ||
                            a.companyId === companyId ||
                            (companyId !== undefined && !a.companyId) ||
                            (companyId === null && !a.companyId))
                )
        },
        [loggedUser.data]
    )

    const getCompanies = useCallback(
        (role: UserRole | UserRole[]) => {
            if (!loggedUser.data) return []
            if (_.isArray(role))
                return loggedUser.data.roles
                    .filter((a) => !!a.companyId && role.includes(a.role))
                    .map((a) => a.companyId!)
            else return loggedUser.data.roles.filter((a) => !!a.companyId && a.role === role).map((a) => a.companyId!)
        },
        [loggedUser.data]
    )

    const getLimits = useCallback(
        (role: UserRole, companyId?: number) => {
            if (!loggedUser.data) return { oneTimeLimit: undefined, yearlyLimit: undefined }
            const roleAssignToCompany = companyId
                ? loggedUser.data.roles.find((a) => a.role === role && a.companyId === companyId)
                : undefined
            const roleAssignToAll = loggedUser.data.roles.find((a) => a.role === role && !a.companyId)

            return {
                oneTimeLimit: roleAssignToCompany?.oneTimeLimit ?? roleAssignToAll?.oneTimeLimit,
                yearlyLimit: roleAssignToCompany?.yearlyLimit ?? roleAssignToAll?.yearlyLimit,
            }
        },
        [loggedUser.data]
    )

    const contextProps: AppAuthProviderContextProps | undefined = useMemo(
        () => ({
            accessToken: loggedUser.data ? tokens?.accessToken : undefined,
            loggedUser: tokens?.accessToken ? loggedUser.data : undefined,
            logout: onLogout,
            setTokens: onLogin,
            hasRole,
            getLimits,
            getCompanies,
            personificateUser,
        }),
        [getCompanies, getLimits, hasRole, loggedUser.data, onLogout, onLogin, tokens?.accessToken, personificateUser]
    )
    return {
        isBusy: loggedUser.isLoading || tokensProps.isBusy,
        contextProps,
        ...props,
    }
}
export default useAppAuthProvider
